
import { LayoutGrid, List } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ViewMode, validLocations } from "@/types/contractor";
import { useServiceTypes } from "@/hooks/useServiceTypes";
import { LoadingState } from "@/components/LoadingState";

interface SearchAndFilterProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onServiceTypeChange: (type: string) => void;
  onLocationChange?: (location: string) => void;
  selectedServiceType?: string;
}

export const SearchAndFilter = ({
  viewMode,
  onViewModeChange,
  onServiceTypeChange,
  onLocationChange,
  selectedServiceType = 'all',
}: SearchAndFilterProps) => {
  const { data: serviceTypes, isLoading, error } = useServiceTypes();
  
  console.log('Rendering SearchAndFilter with:', {
    selectedServiceType,
    availableServiceTypes: serviceTypes,
    isLoading,
    error
  });

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    console.error('Error loading service types:', error);
    return <div>Error loading service types. Please try again later.</div>;
  }

  return (
    <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
      <div className="flex flex-col sm:flex-row gap-4">
        <Select 
          onValueChange={onServiceTypeChange} 
          value={selectedServiceType}
          defaultValue="all"
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by service" />
          </SelectTrigger>
          <SelectContent className="max-h-[300px] overflow-y-auto">
            <SelectItem value="all">All Services</SelectItem>
            {serviceTypes?.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select onValueChange={(value) => onLocationChange?.(value)} defaultValue="all">
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by location" />
          </SelectTrigger>
          <SelectContent className="max-h-[300px] overflow-y-auto">
            <SelectItem value="all">All Locations</SelectItem>
            {validLocations.map((location) => (
              <SelectItem key={location} value={location}>
                {location.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-2">
        <Button
          variant={viewMode === "grid" ? "default" : "outline"}
          size="icon"
          onClick={() => onViewModeChange("grid")}
        >
          <LayoutGrid className="h-4 w-4" />
        </Button>
        <Button
          variant={viewMode === "list" ? "default" : "outline"}
          size="icon"
          onClick={() => onViewModeChange("list")}
        >
          <List className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
